import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import ButtonWithCheckbox from '../CheckupCard/ButtonWithCheckbox';
import Link, { LinkVariants } from '../Link';
import SmartLink from '../SmartLink';
import Badge from '../Badge';

import { Heading6, Body1, Body2, Caption, ButtonText } from '../Typography';
import { ReactComponent as ArrowDownIcon } from '../../assets/svg/accordion-down.svg';

import { useMeasureAccordionContentHeight } from '../../app/hooks/useMeasureAccordionContentHeight';

import styles from './index.module.scss';

const TIMEOUT_DELAY = 1000;

export interface TextActionAccordionProps {
  activeStateTagText?: string;
  number: string;
  title: string;
  subtitle?: string;
  text?: string;
  className?: string;
  isOpenByDefault?: boolean;
  leftButtonText?: string;
  buttonOnClick?: () => void;
  isButtonLoading?: boolean;
  leftButtonError?: object;
  completed?: boolean;
  rightButtonText?: string;
  rightButtonUrl?: string;
  contentLinkText?: string;
  contentLinkUrl?: string;
  children?: React.ReactNode;
}

const TextActionAccordion: React.FC<TextActionAccordionProps> = ({
  activeStateTagText,
  number,
  title,
  subtitle,
  text,
  className,
  isOpenByDefault = false,
  completed,
  leftButtonText,
  buttonOnClick,
  rightButtonText,
  rightButtonUrl,
  isButtonLoading,
  contentLinkText,
  contentLinkUrl,
  leftButtonError,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const { contentRef, contentHeight } = useMeasureAccordionContentHeight();

  useEffect(() => {
    setTimeout(() => {
      setIsOpen(isOpenByDefault);
    }, TIMEOUT_DELAY);
  }, [isOpenByDefault]);

  const handleAccordionClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      className={cx(
        styles.wrapper,
        { [styles.isOpen]: isOpen, [styles.completed]: completed },
        className,
      )}
    >
      {activeStateTagText && (
        <Badge className={styles.badge} text={activeStateTagText} />
      )}
      <button onClick={handleAccordionClick} className={styles.header}>
        <Caption element='span' className={styles.number}>
          {number}
        </Caption>
        <div className={styles.headerTextWrapper}>
          <div className={styles.headerTitleAndIconWrapper}>
            <Heading6 className={styles.title}>{title}</Heading6>
            <ArrowDownIcon
              className={cx({
                [styles.arrowDown]: true,
                [styles.arrowUp]: isOpen,
              })}
            />
          </div>
          {subtitle && <Body2 className={styles.subtitle}>{subtitle}</Body2>}
        </div>
      </button>

      <div
        ref={contentRef}
        className={styles.contentWrapper}
        style={{ maxHeight: isOpen ? contentHeight : 0 }}
      >
        <div className={styles.content}>
          {text && (
            <Body1 element='div' className={styles.text}>
              <Markdown rehypePlugins={[rehypeRaw]}>{text}</Markdown>
            </Body1>
          )}

          {contentLinkText && contentLinkUrl && (
            <SmartLink to={contentLinkUrl} className={styles.contentLink}>
              <ButtonText>{contentLinkText}</ButtonText>
            </SmartLink>
          )}

          {children}

          {(leftButtonText || rightButtonText) && (
            <div
              className={cx(styles.buttonsWrapper, {
                [styles.buttonsWrapperLessMarginTop]: children,
              })}
            >
              {leftButtonText && (
                <ButtonWithCheckbox
                  isButtonLoading={isButtonLoading}
                  isMarkedAsCompleted={completed}
                  handleButtonOnClick={buttonOnClick}
                  error={leftButtonError}
                >
                  {leftButtonText}
                </ButtonWithCheckbox>
              )}

              {rightButtonText && rightButtonUrl && (
                <Link
                  variant={LinkVariants.OUTLINED_GRADIENT}
                  url={rightButtonUrl}
                  size='small'
                  className={styles.rightButton}
                >
                  {rightButtonText}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default TextActionAccordion;
